import React from 'react';

// --- COMPONENTES BÁSICOS ---

export const ColorInput = ({ label, value, onChange }) => {
    const safeValue = (val) => (!val || !val.startsWith('#') || val.length !== 7) ? '#000000' : val;
    
    return (
        <div className="mb-4">
            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{label}</label>
            <div className="flex items-center gap-2">
                <div className="relative w-8 h-8 rounded border shadow-sm overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-200 transition-all">
                    <input 
                        type="color" 
                        value={safeValue(value)} 
                        onChange={(e) => onChange(e.target.value)} 
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer p-0 border-0"
                        title="Escolher cor"
                    />
                    <div className="w-full h-full" style={{ backgroundColor: safeValue(value) }}></div>
                </div>
                <input 
                    type="text" 
                    value={value || ''} 
                    onChange={(e) => onChange(e.target.value)} 
                    className="flex-1 text-xs border rounded p-1.5 font-mono uppercase text-gray-600 focus:border-blue-500 outline-none" 
                    placeholder="#000000" 
                />
            </div>
        </div>
    );
};

export const RangeInput = ({ label, value, min = 0, max = 100, unit = 'px', onChange }) => (
    <div className="mb-4">
        <div className="flex justify-between mb-1">
            <label className="text-[10px] font-bold text-gray-400 uppercase">{label}</label>
            <span className="text-[10px] text-gray-500 font-mono">{value || 0}{unit}</span>
        </div>
        <input 
            type="range" 
            min={min} 
            max={max} 
            value={value || 0} 
            onChange={(e) => onChange(parseInt(e.target.value))} 
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" 
        />
    </div>
);

export const SelectInput = ({ label, value, onChange, options }) => (
    <div className="mb-4">
        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{label}</label>
        <select 
            value={value || ''} 
            onChange={(e) => onChange(e.target.value)} 
            className="w-full p-2 text-xs border rounded bg-white focus:border-blue-500 outline-none"
        >
            {options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
        </select>
    </div>
);

// --- COMPONENTES COMPOSTOS ---

export const TypographyControl = ({ prefix, data, onChange }) => (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 mb-4">
        <h4 className="text-[10px] font-bold text-gray-400 uppercase mb-3 border-b pb-2">Tipografia</h4>
        
        <div className="grid grid-cols-2 gap-3 mb-3">
            {/* Fonte - CAIXA DE TEXTO LIVRE */}
            <div>
                <label className="block text-[9px] text-gray-400 mb-1">Fonte (Nome)</label>
                <input 
                    type="text" 
                    value={data[`${prefix}Font`] || ''} 
                    onChange={(e) => onChange(`${prefix}Font`, e.target.value)} 
                    className="w-full text-[10px] p-1.5 border rounded"
                    placeholder="Ex: Inter, Arial..."
                />
            </div>
            {/* Estilos: Bold, Italic e SOMBRA DE TEXTO */}
            <div>
                <label className="block text-[9px] text-gray-400 mb-1">Estilo</label>
                <div className="flex bg-white rounded border">
                    <button onClick={() => onChange(`${prefix}Bold`, !data[`${prefix}Bold`])} className={`flex-1 py-1 text-[10px] ${data[`${prefix}Bold`] ? 'bg-blue-50 text-blue-600 font-bold' : 'text-gray-400'}`} title="Negrito">B</button>
                    <button onClick={() => onChange(`${prefix}Italic`, !data[`${prefix}Italic`])} className={`flex-1 py-1 text-[10px] border-l ${data[`${prefix}Italic`] ? 'bg-blue-50 text-blue-600 italic' : 'text-gray-400'}`} title="Itálico">I</button>
                    {/* AQUI ESTÁ A SOMBRA DE TEXTO */}
                    <button onClick={() => onChange(`${prefix}TextShadow`, !data[`${prefix}TextShadow`])} className={`flex-1 py-1 text-[10px] border-l ${data[`${prefix}TextShadow`] ? 'bg-blue-50 text-blue-600 font-bold' : 'text-gray-400'}`} title="Sombra Texto">S</button>
                </div>
            </div>
        </div>

        <RangeInput label="Tamanho" value={data[`${prefix}Size`]} onChange={(v) => onChange(`${prefix}Size`, v)} unit="px" min={10} max={100} />
        <ColorInput label="Cor do Texto" value={data[`${prefix}Color`]} onChange={(v) => onChange(`${prefix}Color`, v)} />
    </div>
);

export const BoxControl = ({ prefix, data, onChange }) => (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 mb-4">
        <h4 className="text-[10px] font-bold text-gray-400 uppercase mb-3 border-b pb-2">Caixa & Fundo</h4>
        <ColorInput label="Fundo" value={data[`${prefix}BgColor`]} onChange={(v) => onChange(`${prefix}BgColor`, v)} />
        <ColorInput label="Borda" value={data[`${prefix}BorderColor`]} onChange={(v) => onChange(`${prefix}BorderColor`, v)} />
        
        <div className="grid grid-cols-2 gap-3">
            <RangeInput label="Opacidade" value={data[`${prefix}Opacity`]} onChange={(v) => onChange(`${prefix}Opacity`, v)} unit="%" />
            <RangeInput label="Arredondar" value={data[`${prefix}Radius`]} onChange={(v) => onChange(`${prefix}Radius`, v)} unit="px" />
        </div>
        
        {/* SOMBRA DA CAIXA e BLUR */}
        <div className="grid grid-cols-2 gap-3 mt-2">
            <RangeInput label="Sombra Caixa" value={data[`${prefix}Shadow`]} onChange={(v) => onChange(`${prefix}Shadow`, v)} unit="px" max={50} />
            <RangeInput label="Blur (Desfoque)" value={data[`${prefix}Blur`]} onChange={(v) => onChange(`${prefix}Blur`, v)} unit="px" max={20} />
        </div>
    </div>
);

export const LayoutControl = ({ prefix, data, onChange }) => (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 mb-4">
        <h4 className="text-[10px] font-bold text-gray-400 uppercase mb-3 border-b pb-2">Espaçamento</h4>
        <RangeInput label="Padding" value={data[`${prefix}Padding`]} onChange={(v) => onChange(`${prefix}Padding`, v)} unit="px" />
        <RangeInput label="Largura Máx." value={data[`${prefix}Width`]} onChange={(v) => onChange(`${prefix}Width`, v)} unit="%" />
    </div>
);

export const PositionControl = ({ prefix, data, onChange }) => (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 mb-4">
        <h4 className="text-[10px] font-bold text-gray-400 uppercase mb-3 border-b pb-2">Posição</h4>
        <div className="grid grid-cols-2 gap-3">
            <RangeInput label="Vertical (Y)" value={data[`${prefix}Top`]} onChange={(v) => onChange(`${prefix}Top`, v)} unit="%" />
            <RangeInput label="Horizontal (X)" value={data[`${prefix}Left`]} onChange={(v) => onChange(`${prefix}Left`, v)} unit="%" />
        </div>
    </div>
);